import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Paper } from "@mui/material";
import CustomTextArea from "../../app/common/form/CustomTextArea";
import CustomTextInput from "../../app/common/form/CustomTextInput";
import * as Yup from "yup";
import { v4 as uuid } from "uuid";
import { useStore } from "../../app/stores/store";
import LoadingComponent from "../../app/layout/LoadingComponent";
import {
  FamilyMember,
  FamilyMemberFormValues,
} from "../../app/models/familyMember";
import CustomDateInput from "../../app/common/form/CustomDateInput";
import CustomCheckbox from "../../app/common/form/CustomCheckBox";
import CustomSelectInput from "../../app/common/form/CustomSelectInput";
import { useRelationShipTypeOptions } from "../../app/common/enums/relationType";
import { useTranslation } from "react-i18next";

interface Props {
  id?: string;
}

export default function FamilyMemberForm({ id }: Props) {
  const { t } = useTranslation();
  const [familyMember, setFamilyMember] = useState<FamilyMemberFormValues>(
    new FamilyMemberFormValues()
  );
  const familyMemberStore = useStore((s) => s.familyMemberStore);
  const modalStore = useStore((s) => s.modalStore);
  const memberStore = useStore((s) => s.memberStore);
  const { createFamilyMember, updateFamilyMember, loading } = familyMemberStore;
  const relationShipTypeOptions = useRelationShipTypeOptions();
  const commonRequiredMessage = t("familyMemberForm.requiredMessage");
  const validationSchema = Yup.object({
    firstName: Yup.string().required(
      `${t("familyMemberForm.firstNameLabel")} ${commonRequiredMessage}`
    ),
    lastName: Yup.string().required(
      `${t("familyMemberForm.lastNameLabel")} ${commonRequiredMessage}`
    ),
  });

  const handleFormSubmit = async (values: FamilyMemberFormValues) => {
    if (!values.id) {
      const newFamilyMember: FamilyMember = {
        ...values,
        id: uuid(),
        memberId: memberStore.selectedMember?.id!,
      };
      await createFamilyMember(newFamilyMember as any);
      modalStore.closeModal();
    } else {
      const updated: FamilyMember = { ...values, id };
      await updateFamilyMember(updated as any);
      modalStore.closeModal();
    }
  };

  useEffect(() => {
    if (!id) return;
    familyMemberStore.loadFamilyMember(id).then((fm: any) =>
      setFamilyMember(new FamilyMemberFormValues(fm))
    );
  }, [id, familyMemberStore]);

  if (loading)
    return <LoadingComponent content={t("familyMemberForm.loading")} />;

  return (
    <Paper sx={{ p: 2 }}>
      <Formik
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={{
          ...familyMember,
          ...((!familyMember.id && {
            lastName: memberStore.selectedMember?.lastName || "",
            mekteb: familyMember.mekteb ?? false,
          }) as any),
        }}
        onSubmit={(values) => handleFormSubmit(values)}
      >
        {({ handleSubmit, isValid, isSubmitting, dirty }) => (
          <form
            onSubmit={handleSubmit}
            autoComplete="off"
            style={{ paddingBottom: "35px" }}
          >
            <div className="two fields">
              <CustomTextInput
                type="text"
                name="firstName"
                placeholder={t("familyMemberForm.firstNamePlaceholder")}
                label={t("familyMemberForm.firstNameLabel")}
              />
              <CustomTextInput
                type="text"
                name="lastName"
                placeholder={t("familyMemberForm.lastNamePlaceholder")}
                label={t("familyMemberForm.lastNameLabel")}
              />
            </div>

            <div className="three fields">
              <CustomDateInput
                name="dateOfBirth"
                label={t("familyMemberForm.dateOfBirthLabel")}
                placeholderText={t("familyMemberForm.dateOfBirthLabel")}
                dateFormat="dd.MM.yyyy"
              />
              <CustomSelectInput
                options={relationShipTypeOptions}
                name="relationShipType"
                placeholder={t("familyMemberForm.relationShipTypeLabel")}
                label={t("familyMemberForm.relationShipTypeLabel")}
              />
              <CustomCheckbox
                name="mekteb"
                label={t("familyMemberForm.mektebLabel")}
              />
            </div>

            <CustomTextArea
              label={t("familyMemberForm.notesLabel")}
              rows={3}
              name="note"
              placeholder={t("familyMemberForm.notesLabel")}
            />

            <Button
              variant="contained"
              color="primary"
              disabled={isSubmitting || !dirty || !isValid}
              type="submit"
              sx={{ float: "right", mt: 2, ml: 2 }}
            >
              {t("general.confirm")}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={modalStore.closeModal}
              type="button"
              sx={{ float: "right", mt: 2 }}
            >
              {t("general.cancel")}
            </Button>
          </form>
        )}
      </Formik>
    </Paper>
  );
}
